import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Car, MessageSquare, Plus, Edit, Trash2, LogOut, Eye } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { useToast } from '../../hooks/use-toast';
import { getVehicles, deleteVehicle, getMessages } from '../../services/api';

export const AdminDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [vehicles, setVehicles] = useState([]);
  const [messages, setMessages] = useState([]);

  useEffect(() => {
    // Check auth
    if (localStorage.getItem('adminAuth') !== 'true') {
      navigate('/admin');
      return;
    }

    // Load vehicles and messages from API
    const loadData = async () => {
      try {
        const vehiclesResponse = await getVehicles();
        setVehicles(vehiclesResponse.data);
        
        const messagesResponse = await getMessages();
        setMessages(messagesResponse.data);
      } catch (error) {
        console.error('Error loading dashboard data:', error);
      }
    };
    
    loadData();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('adminAuth');
    toast({
      title: "Logged Out",
      description: "You have been successfully logged out.",
    });
    navigate('/admin');
  };

  const handleDeleteVehicle = async (id) => {
    if (window.confirm('Are you sure you want to delete this vehicle?')) {
      try {
        await deleteVehicle(id);
        
        // Refresh vehicles list
        const response = await getVehicles();
        setVehicles(response.data);
        
        toast({
          title: "Vehicle Deleted",
          description: "The vehicle has been removed from inventory.",
        });
      } catch (error) {
        console.error('Error deleting vehicle:', error);
        toast({
          title: "Error",
          description: "Failed to delete vehicle. Please try again.",
          variant: "destructive"
        });
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
            <div className="flex items-center space-x-4">
              <Link to="/" target="_blank">
                <Button variant="outline">
                  <Eye className="mr-2 h-4 w-4" />
                  View Site
                </Button>
              </Link>
              <Button variant="outline" onClick={handleLogout}>
                <LogOut className="mr-2 h-4 w-4" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Vehicles</p>
                  <p className="text-3xl font-bold text-gray-900">{vehicles.length}</p>
                </div>
                <div className="bg-teal-100 p-3 rounded-full">
                  <Car className="h-6 w-6 text-teal-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Contact Messages</p>
                  <p className="text-3xl font-bold text-gray-900">{messages.length}</p>
                </div>
                <div className="bg-teal-100 p-3 rounded-full">
                  <MessageSquare className="h-6 w-6 text-teal-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Inventory Value</p>
                  <p className="text-3xl font-bold text-gray-900">
                    ${vehicles.reduce((sum, v) => sum + v.price, 0).toLocaleString()}
                  </p>
                </div>
                <div className="bg-teal-100 p-3 rounded-full">
                  <span className="text-teal-600 font-bold text-xl">$</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Vehicle Management */}
        <Card className="mb-12">
          <CardContent className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Vehicle Inventory</h2>
              <Link to="/admin/vehicle/new">
                <Button className="bg-teal-600 hover:bg-teal-700 text-white">
                  <Plus className="mr-2 h-4 w-4" />
                  Add Vehicle
                </Button>
              </Link>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vehicle</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Year</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mileage</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Condition</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {vehicles.map((vehicle) => (
                    <tr key={vehicle.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <img src={vehicle.images[0]} alt="" className="h-10 w-16 object-cover rounded" />
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{vehicle.make} {vehicle.model}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{vehicle.year}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">${vehicle.price.toLocaleString()}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{vehicle.mileage.toLocaleString()}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-teal-100 text-teal-800">
                          {vehicle.condition}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                        <Link to={`/admin/vehicle/${vehicle.id}`}>
                          <Button variant="ghost" size="sm">
                            <Edit className="h-4 w-4" />
                          </Button>
                        </Link>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteVehicle(vehicle.id)}>
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Messages */}
        <Card>
          <CardContent className="p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Recent Messages</h2>
            {messages.length === 0 ? (
              <p className="text-gray-600 text-center py-8">No messages yet</p>
            ) : (
              <div className="space-y-4">
                {messages.slice(-5).reverse().map((message) => (
                  <Card key={message.id} className="border">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="font-semibold text-gray-900">{message.name}</p>
                          <p className="text-sm text-gray-600">{message.email}</p>
                          {message.phone && <p className="text-sm text-gray-600">{message.phone}</p>}
                        </div>
                        <p className="text-xs text-gray-500">
                          {new Date(message.date).toLocaleDateString()}
                        </p>
                      </div>
                      <p className="text-gray-700">{message.message}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
